/**
 * AllyModifierService - Grant roll modifiers to party members via RTDB
 *
 * Supports modifier types:
 * - reroll: Allow ally to reroll their Duality Dice (or specific die)
 * - advantage: Grant advantage on next roll
 * - bonus: Add flat bonus to next roll
 *
 * Data Model (Firebase RTDB):
 * /campaigns/{campaignId}/allyModifiers/{targetCharacterId}/{modifierId}
 * {
 *   type: "reroll" | "advantage" | "bonus",
 *   value: number | null,
 *   source: "Reassurance",
 *   sourceCharacterId: "char_abc",
 *   sourceName: "Sal Newton",
 *   expiresOn: "nextRoll" | "nextActionRoll" | "endOfTurn",
 *   rerollTarget: "hope" | "fear" | "any",  // For reroll type
 *   createdAt: serverTimestamp()
 * }
 *
 * Usage:
 *   await grantAllyModifier(campaignId, targetCharacterId, modifier)
 *   const modifiers = await getAllyModifiers(campaignId, characterId)
 *   await consumeAllyModifier(campaignId, characterId, modifierId)
 */

import { rtdb } from '../firebase/config';
import { ref, set, get, remove, push, serverTimestamp } from 'firebase/database';

/**
 * Modifier type definitions
 * @typedef {'reroll' | 'advantage' | 'bonus'} ModifierType
 * @typedef {'nextRoll' | 'nextActionRoll' | 'endOfTurn'} ExpiresOn
 * @typedef {'hope' | 'fear' | 'any'} RerollTarget
 *
 * @typedef {Object} AllyModifier
 * @property {string} id - Auto-generated modifier ID
 * @property {ModifierType} type - Type of modifier
 * @property {number|null} value - Bonus value (for bonus type)
 * @property {string} source - Name of the ability granting this modifier
 * @property {string} sourceCharacterId - Character ID of the granter
 * @property {string} sourceName - Display name of the granter
 * @property {ExpiresOn} expiresOn - When this modifier expires
 * @property {RerollTarget} [rerollTarget] - Which die can be rerolled (for reroll type)
 * @property {Object} createdAt - Server timestamp
 */

/**
 * Grant a modifier to an ally
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} targetCharacterId - Character ID of the recipient
 * @param {Object} modifier - Modifier data
 * @param {ModifierType} modifier.type - Type of modifier
 * @param {number|null} modifier.value - Bonus value (for bonus type)
 * @param {string} modifier.source - Name of the ability granting this
 * @param {string} modifier.sourceCharacterId - Character ID of the granter
 * @param {string} modifier.sourceName - Display name of the granter
 * @param {ExpiresOn} modifier.expiresOn - When this modifier expires
 * @param {RerollTarget} [modifier.rerollTarget] - Which die can be rerolled
 * @returns {Promise<string>} The generated modifier ID
 */
export const grantAllyModifier = async (campaignId, targetCharacterId, modifier) => {
  if (!campaignId || !targetCharacterId) {
    throw new Error('campaignId and targetCharacterId are required');
  }

  const modifiersRef = ref(rtdb, `campaigns/${campaignId}/allyModifiers/${targetCharacterId}`);
  const newModifierRef = push(modifiersRef);

  const modifierData = {
    id: newModifierRef.key,
    type: modifier.type,
    value: modifier.value ?? null,
    source: modifier.source,
    sourceCharacterId: modifier.sourceCharacterId,
    sourceName: modifier.sourceName,
    expiresOn: modifier.expiresOn || 'nextRoll',
    rerollTarget: modifier.rerollTarget || 'any',
    createdAt: serverTimestamp()
  };

  await set(newModifierRef, modifierData);
  return newModifierRef.key;
};

/**
 * Get all pending modifiers for a character
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} characterId - Character ID to check modifiers for
 * @returns {Promise<AllyModifier[]>} Array of pending modifiers
 */
export const getAllyModifiers = async (campaignId, characterId) => {
  if (!campaignId || !characterId) {
    return [];
  }

  const modifiersRef = ref(rtdb, `campaigns/${campaignId}/allyModifiers/${characterId}`);
  const snapshot = await get(modifiersRef);

  if (!snapshot.exists()) {
    return [];
  }

  const modifiersData = snapshot.val();
  return Object.values(modifiersData);
};

/**
 * Check if a character has any pending modifiers
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} characterId - Character ID to check
 * @returns {Promise<boolean>} True if there are pending modifiers
 */
export const hasAllyModifiers = async (campaignId, characterId) => {
  const modifiers = await getAllyModifiers(campaignId, characterId);
  return modifiers.length > 0;
};

/**
 * Get modifiers of a specific type
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} characterId - Character ID
 * @param {ModifierType} type - Type to filter by
 * @returns {Promise<AllyModifier[]>} Filtered modifiers
 */
export const getAllyModifiersByType = async (campaignId, characterId, type) => {
  const modifiers = await getAllyModifiers(campaignId, characterId);
  return modifiers.filter(m => m.type === type);
};

/**
 * Consume (remove) a specific modifier after use
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} characterId - Character ID
 * @param {string} modifierId - Modifier ID to remove
 * @returns {Promise<void>}
 */
export const consumeAllyModifier = async (campaignId, characterId, modifierId) => {
  if (!campaignId || !characterId || !modifierId) {
    return;
  }

  const modifierRef = ref(rtdb, `campaigns/${campaignId}/allyModifiers/${characterId}/${modifierId}`);
  await remove(modifierRef);
};

/**
 * Consume all modifiers that expire on a specific trigger
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} characterId - Character ID
 * @param {ExpiresOn} trigger - Expiration trigger to match
 * @returns {Promise<AllyModifier[]>} The modifiers that were consumed
 */
export const consumeModifiersByTrigger = async (campaignId, characterId, trigger) => {
  const modifiers = await getAllyModifiers(campaignId, characterId);
  const toConsume = modifiers.filter(m => m.expiresOn === trigger);

  for (const modifier of toConsume) {
    await consumeAllyModifier(campaignId, characterId, modifier.id);
  }

  return toConsume;
};

/**
 * Clear all modifiers for a character (e.g., on session end)
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} characterId - Character ID
 * @returns {Promise<void>}
 */
export const clearAllModifiers = async (campaignId, characterId) => {
  if (!campaignId || !characterId) {
    return;
  }

  const modifiersRef = ref(rtdb, `campaigns/${campaignId}/allyModifiers/${characterId}`);
  await remove(modifiersRef);
};

/**
 * Helper to create a reroll modifier (e.g., from Reassurance)
 *
 * @param {Object} source - Source character info
 * @param {string} source.characterId - Source character ID
 * @param {string} source.name - Source character name
 * @param {string} abilityName - Name of the ability
 * @param {RerollTarget} [rerollTarget='any'] - Which die can be rerolled
 * @returns {Object} Modifier data ready to pass to grantAllyModifier
 */
export const createRerollModifier = (source, abilityName, rerollTarget = 'any') => ({
  type: 'reroll',
  value: null,
  source: abilityName,
  sourceCharacterId: source.characterId,
  sourceName: source.name,
  expiresOn: 'nextRoll',
  rerollTarget
});

/**
 * Helper to create an advantage modifier (e.g., from Lead By Example)
 *
 * @param {Object} source - Source character info
 * @param {string} abilityName - Name of the ability
 * @param {ExpiresOn} [expiresOn='nextRoll'] - When to expire
 * @param {number} [dieSize=6] - Size of advantage die (6=d6, 8=d8, 10=d10)
 * @returns {Object} Modifier data
 */
export const createAdvantageModifier = (source, abilityName, expiresOn = 'nextRoll', dieSize = 6) => ({
  type: 'advantage',
  value: null,
  advantageDieSize: dieSize,
  source: abilityName,
  sourceCharacterId: source.characterId,
  sourceName: source.name,
  expiresOn
});

/**
 * Helper to create a bonus modifier
 *
 * @param {Object} source - Source character info
 * @param {string} abilityName - Name of the ability
 * @param {number} bonus - Flat bonus to add
 * @param {ExpiresOn} [expiresOn='nextRoll'] - When to expire
 * @returns {Object} Modifier data
 */
export const createBonusModifier = (source, abilityName, bonus, expiresOn = 'nextRoll') => ({
  type: 'bonus',
  value: bonus,
  source: abilityName,
  sourceCharacterId: source.characterId,
  sourceName: source.name,
  expiresOn
});

export default {
  grantAllyModifier,
  getAllyModifiers,
  hasAllyModifiers,
  getAllyModifiersByType,
  consumeAllyModifier,
  consumeModifiersByTrigger,
  clearAllModifiers,
  createRerollModifier,
  createAdvantageModifier,
  createBonusModifier
};
